import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { NgbModule } from '@cmps/ng-bootstrap';
import { NgChartModule } from '@cmps/ng-chart';
import { SelectModule } from '@cmps/ng-select';
import {
    CmpsButtonModule,
    CmpsChipModule,
    CmpsContainerModule,
    CmpsDialogModule,
    CmpsFilterModule,
    CmpsIconModule,
    CmpsIconRegistry,
    CmpsPortalModule,
    CmpsSlideBarModule,
    CmpsTabButtonModule,
    CmpsTabsetModule,
    CmpsTooltipModule,
} from '@cmps/ng-uikit';

import { CmpsCommonModule } from '../common/common.module';
import { CmpsLazyI18nModule } from '../i18n/i18n.module';
import { I18nService } from '../i18n/i18n.service';
import { CmpsLazyRouterModule } from '../route.module';

import { ScYesNoDisplayComponent } from './_common';
import { ScorecardDataService } from './core/data.service';
import { ScorecardExportService } from './core/export.service';
import { ScLayoutContainerComponent } from './core/layout-container.component';
import { ScorecardService } from './core/scorecard.service';
import { ExperimentalNgGridModule } from './experimental.grid';
import { ScorecardComponent } from './main-page/scorecard.component';
import { CpmsMessagesComponent } from './messages/messages.component';
import { CmpsNumberWithSign } from './numberWithSign.pipe';
import { ScHeaderModule } from './sc-header/sc-header.module';
import { TranslationIdMap } from './scorecard.i18n';
import { ScorecardRoutes } from './scorecard.route';
import {
    ScCompBonusGridModalComponent,
    ScCompBonusWidgetComponent,
    ScCompConditionWidgetComponent,
    ScCompModifierWidgetComponent,
    ScCompQualifiersWidgetComponent,
    ScFyiWidgetComponent,
    ScGridComponent,
    ScOverallScoreWidgetComponent,
    ScRankAndScorecardPublishedStatusFilterComponent,
    ScRanksWidgetComponent,
    ScScorecardTrendChartWidgetComponent,
    ScSupergroupFilterComponent,
} from './widgets';
import { CompensationDescriptionPipe } from './widgets/compensation-description.pipe';
import { DirectReportContributionGridComponent } from './widgets/grid/direct-report-contribution-grid.component';
import { CmpsGridPeriodPipe } from './widgets/grid/direct-report-period.pipe';
import { DirectReportrankGridComponent } from './widgets/grid/direct-report-rank-grid.component';
import { DirectReportScorecardGridComponent } from './widgets/grid/direct-report-scorecard-grid.component';
import { GridEpicBridge } from './widgets/grid/grid.epic.bridge';
import { PerformanceGridComponent } from './widgets/grid/performance-grid.component';
import { TeamGridComponent } from './widgets/grid/team-grid.component';

@NgModule({
    declarations: [
        ScCompBonusGridModalComponent,
        ScorecardComponent,
        ScCompBonusWidgetComponent,
        ScCompConditionWidgetComponent,
        ScCompQualifiersWidgetComponent,
        ScCompModifierWidgetComponent,
        ScFyiWidgetComponent,
        ScOverallScoreWidgetComponent,
        ScRankAndScorecardPublishedStatusFilterComponent,
        ScRanksWidgetComponent,
        ScScorecardTrendChartWidgetComponent,
        ScSupergroupFilterComponent,
        ScSupergroupFilterComponent,
        ScYesNoDisplayComponent,
        PerformanceGridComponent,
        DirectReportContributionGridComponent,
        DirectReportrankGridComponent,
        DirectReportScorecardGridComponent,
        TeamGridComponent,
        ScGridComponent,
        ScLayoutContainerComponent,
        CompensationDescriptionPipe,
        CpmsMessagesComponent,
        CmpsGridPeriodPipe,
        CmpsNumberWithSign,
    ],
    imports: [
        CmpsCommonModule,
        FormsModule,
        BrowserModule,
        NgbModule,
        NgChartModule,
        ScHeaderModule,
        CmpsLazyI18nModule.forChild(TranslationIdMap),
        CmpsLazyRouterModule.forChild(ScorecardRoutes),
        ExperimentalNgGridModule,
        SelectModule,
        CmpsContainerModule,
        CmpsIconModule,
        CmpsSlideBarModule,
        CmpsTabsetModule,
        CmpsTabButtonModule,
        CmpsButtonModule,
        CmpsTooltipModule,
        CmpsDialogModule,
        CmpsChipModule,
        CmpsFilterModule,
        CmpsPortalModule,
    ],
    providers: [
        {
            provide: CmpsNumberWithSign,
            useClass: CmpsNumberWithSign,
            deps: [I18nService],
        },
        ScorecardDataService,
    ],
    exports: [ScorecardComponent],
})
export class CmpsScorecardModule {
    static forRoot(): ModuleWithProviders<CmpsScorecardModule> {
        return {
            ngModule: CmpsScorecardModule,
            providers: [
                ScorecardService,
                ScorecardExportService,
                CompensationDescriptionPipe,
                GridEpicBridge,
            ],
        };
    }

    constructor(domSanitizer: DomSanitizer, iconRegistry: CmpsIconRegistry) {
        iconRegistry.addSvgIcon(
            'compact',
            domSanitizer.bypassSecurityTrustResourceUrl('./assets/compact.svg'),
            'scorecard',
        );
        iconRegistry.addSvgIcon(
            'wide',
            domSanitizer.bypassSecurityTrustResourceUrl('./assets/wide.svg'),
            'scorecard',
        );
        iconRegistry.addSvgIcon(
            'left',
            domSanitizer.bypassSecurityTrustResourceUrl('./assets/left.svg'),
            'scorecard',
        );
        iconRegistry.addSvgIcon(
            'right',
            domSanitizer.bypassSecurityTrustResourceUrl('./assets/right.svg'),
            'scorecard',
        );
        iconRegistry.addSvgIcon(
            'left-alt-2',
            domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/left-alt-2.svg',
            ),
            'scorecard',
        );
        iconRegistry.addSvgIcon(
            'right-alt-2',
            domSanitizer.bypassSecurityTrustResourceUrl(
                './assets/right-alt-2.svg',
            ),
            'scorecard',
        );
        iconRegistry.addSvgIcon(
            'trend',
            domSanitizer.bypassSecurityTrustResourceUrl('./assets/trend.svg'),
            'scorecard',
        );
    }
}
